const ELEMENT_TIMEOUT_MS = 8000;
const SEEK_TIMEOUT_MS = 4000;
const MAX_DECODE_BYTES = 20 * 1024 * 1024;

export function positiveFiniteDuration(value) {
  if (value == null || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// Chrome MediaRecorder WebM leaves Duration unset, so the element reports Infinity
// until it is forced to inspect the last cluster.
export function forceElementDuration(element, { timeoutMs = SEEK_TIMEOUT_MS, signal } = {}) {
  return new Promise((resolve) => {
    const existing = positiveFiniteDuration(element?.duration);
    if (!element || existing || signal?.aborted) {
      resolve(existing);
      return;
    }

    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      element.removeEventListener('durationchange', onMaybe);
      element.removeEventListener('timeupdate', onMaybe);
      signal?.removeEventListener('abort', onAbort);
      resolve(positiveFiniteDuration(value));
    };
    const onMaybe = () => {
      const next = positiveFiniteDuration(element.duration);
      if (next) finish(next);
    };
    const onAbort = () => finish(0);
    const timer = setTimeout(() => finish(element.duration), timeoutMs);

    element.addEventListener('durationchange', onMaybe);
    element.addEventListener('timeupdate', onMaybe);
    signal?.addEventListener('abort', onAbort, { once: true });

    try {
      element.currentTime = 1e101;
    } catch {
      finish(0);
    }
  });
}

function releaseElement(element, url) {
  URL.revokeObjectURL(url);
  element.removeAttribute('src');
  element.load();
}

function durationFromElement(file) {
  return new Promise((resolve) => {
    const element = file.type?.startsWith('video/')
      ? document.createElement('video')
      : document.createElement('audio');
    const url = URL.createObjectURL(file);
    const controller = new AbortController();
    let settled = false;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      controller.abort();
      releaseElement(element, url);
      resolve(positiveFiniteDuration(value));
    };

    const timer = setTimeout(() => finish(0), ELEMENT_TIMEOUT_MS);
    let metadataSeen = false;
    element.preload = 'metadata';
    element.addEventListener('error', () => {
      if (!metadataSeen) finish(0);
    });
    element.addEventListener('loadedmetadata', () => {
      metadataSeen = true;
      clearTimeout(timer);
      const direct = positiveFiniteDuration(element.duration);
      if (direct) {
        finish(direct);
        return;
      }
      forceElementDuration(element, {
        timeoutMs: SEEK_TIMEOUT_MS,
        signal: controller.signal,
      }).then(finish);
    });
    element.src = url;
  });
}

async function durationFromDecode(file) {
  if (!file?.size || file.size > MAX_DECODE_BYTES) return 0;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return 0;

  let context;
  try {
    context = new AudioCtx();
    if (context.state === 'suspended') await context.resume();
    const raw = await file.arrayBuffer();
    const audioBuffer = await context.decodeAudioData(raw.slice(0));
    return positiveFiniteDuration(audioBuffer?.duration);
  } catch {
    return 0;
  } finally {
    try {
      await context?.close();
    } catch {
      // ignore close errors
    }
  }
}

export async function readAudioDuration(file) {
  if (!file?.size) return 0;
  try {
    const fromElement = await durationFromElement(file);
    if (fromElement) return fromElement;
    return await durationFromDecode(file);
  } catch {
    return 0;
  }
}
